import { AgentThread } from '@dreamai/types';

export interface SpecialMessageArtifact {
  type:
    | 'linkToOtherThread'
    | 'authorizationLink'
    | 'googleFlightsLink'
    | 'imagesToView'
    | 'filesToView'
    | 'updateToProDoc'
    | 'campaignCreationLink';
  data: {
    threadId?: string;
    agentId?: string;
    authorizationKey?: string;
    bookingKey?: string;
    shortSummary?: string;
    docDescriptor?: string;
    contextId?: string; // For campaignCreationLink
    images?: Array<{
      url: string;
      filename?: string;
      caption?: string;
    }>;
    files?: Array<{
      url: string;
      filename: string;
      mimeType?: string;
    }>;
  };
}

export interface MessageMetadata {
  confirmationId?: string;
  specialMessageArtifacts?: SpecialMessageArtifact[];
  userId?: string;
  userName?: string;
  hiddenFromUser?: boolean;
}

/**
 * Extracts the 'response' field from a partially constructed JSON string
 * This allows us to display streaming content as it arrives, even before
 * the complete JSON object is formed
 *
 * @param partialJson The partial JSON string being constructed
 * @returns The current value of the response field, or empty string if not found
 *          or if showToUser is false
 */
export const extractResponseFromPartialJson = (partialJson: string): string => {
  // If the string is empty or doesn't look like JSON, return empty string
  if (!partialJson || !partialJson.includes('{')) {
    return '';
  }

  // Check if this might be plain text rather than JSON
  // If it doesn't have quotes around keys or values, it's likely not JSON
  if (!partialJson.includes('"') && !partialJson.includes("'")) {
    return '';
  }

  // Check for showToUser: false in partial JSON
  const showToUserFalsePattern = /"showToUser"\s*:\s*false\b/;
  if (showToUserFalsePattern.test(partialJson)) {
    return '';
  }

  try {
    // Check if we have a complete valid JSON
    try {
      const parsed = JSON.parse(partialJson);
      // Return empty string if showToUser is explicitly set to false
      if (parsed.showToUser === false) {
        return '';
      }
      return parsed.response || '';
    } catch {
      // Not valid JSON yet, continue with partial parsing
    }

    // Look for the response field pattern
    const responseMatch = partialJson.match(
      /"response"\s*:\s*"([^]*?)(?:"\s*,|"\s*}|"$)/,
    );
    if (responseMatch && responseMatch[1]) {
      // Handle escaped quotes and other escape sequences
      return responseMatch[1].replace(/\\(.)/g, '$1');
    }

    // If we can't find a proper match but we see the response key starting
    if (
      partialJson.includes('"response"') ||
      partialJson.includes("'response'")
    ) {
      // Find where the value starts
      const valueStart =
        partialJson.indexOf(':', partialJson.indexOf('response')) + 1;
      if (valueStart > 0) {
        // Extract everything after the colon
        let value = partialJson.substring(valueStart);

        // Trim leading whitespace and the first quote if present
        value = value.trimStart();
        if (value.startsWith('"') || value.startsWith("'")) {
          value = value.substring(1);
        }

        // If we have a comma or closing brace after the value, trim there
        const commaPos = value.lastIndexOf(',');
        const bracePos = value.lastIndexOf('}');
        const quotePos = value.lastIndexOf('"');

        if (
          quotePos > 0 &&
          (quotePos < commaPos || commaPos === -1) &&
          (quotePos < bracePos || bracePos === -1)
        ) {
          value = value.substring(0, quotePos);
        }

        return value;
      }
    }

    return '';
  } catch (error) {
    console.error('Error parsing partial JSON:', error);
    return 'Agent error, please try again';
  }
};

/**
 * Extracts metadata from a partially constructed JSON string
 * This allows us to extract metadata such as confirmationId as it arrives
 * even before the complete JSON object is formed
 *
 * @param partialJson The partial JSON string being constructed
 * @returns An object containing metadata fields found in the JSON
 */
export const extractMetadataFromPartialJson = (
  partialJson: string,
): MessageMetadata => {
  // If the string is empty or doesn't look like JSON, return empty object
  if (!partialJson || !partialJson.includes('{')) {
    return {};
  }

  // Check if this might be plain text rather than JSON
  // If it doesn't have quotes around keys or values, it's likely not JSON
  if (!partialJson.includes('"') && !partialJson.includes("'")) {
    return {};
  }

  try {
    // Check if we have a complete valid JSON
    try {
      const parsed = JSON.parse(partialJson);
      const metadata: MessageMetadata = {};

      // Check for metadata in the parsed JSON
      if (parsed.metadata) {
        Object.assign(metadata, parsed.metadata);
      }

      // Check for confirmationId at the top level if not in metadata
      if (parsed.confirmationId && !metadata.confirmationId) {
        metadata.confirmationId = parsed.confirmationId;
      }

      // Check for specialMessageArtifacts at the top level if not in metadata
      if (parsed.specialMessageArtifacts && !metadata.specialMessageArtifacts) {
        metadata.specialMessageArtifacts = parsed.specialMessageArtifacts;
      }

      return metadata;
    } catch {
      // Not valid JSON yet, continue with partial parsing
    }

    const metadata: MessageMetadata = {};

    // Look for confirmationId in metadata or at the top level
    const metadataMatch = partialJson.match(/"metadata"\s*:\s*(\{[^}]*\})/);
    if (metadataMatch && metadataMatch[1]) {
      try {
        // Try to parse the metadata object - this ensures all properties are complete
        const parsedMetadata = JSON.parse(metadataMatch[1]);
        Object.assign(metadata, parsedMetadata);
      } catch {
        // Couldn't parse metadata object, try regex for confirmationId
        // Match if there's a closing quote followed by comma, closing brace, or any whitespace
        // This ensures we only match complete property values
        const confirmationIdInMetadataMatch = partialJson.match(
          /"metadata"[^}]*"confirmationId"\s*:\s*"([^"]*)"(?:\s*[,}]|\s*$)/,
        );
        if (confirmationIdInMetadataMatch && confirmationIdInMetadataMatch[1]) {
          metadata.confirmationId = confirmationIdInMetadataMatch[1];
        }

        // Try regex for specialMessageArtifacts in metadata
        const specialArtifactsInMetadataMatch = partialJson.match(
          /"metadata"[^}]*"specialMessageArtifacts"\s*:\s*(\[[^\]]*\])/,
        );
        if (
          specialArtifactsInMetadataMatch &&
          specialArtifactsInMetadataMatch[1]
        ) {
          try {
            metadata.specialMessageArtifacts = JSON.parse(
              specialArtifactsInMetadataMatch[1],
            );
          } catch {
            // Ignore if can't parse the array
          }
        }
      }
    }

    // If confirmationId wasn't in metadata, look for it at the top level
    if (!metadata.confirmationId) {
      // Match if there's a closing quote followed by comma, closing brace, or any whitespace
      // This ensures we only match complete property values
      const confirmationIdMatch = partialJson.match(
        /"confirmationId"\s*:\s*"([^"]*)"(?:\s*[,}]|\s*$)/,
      );
      if (confirmationIdMatch && confirmationIdMatch[1]) {
        metadata.confirmationId = confirmationIdMatch[1];
      }
    }

    // If specialMessageArtifacts wasn't in metadata, look for it at the top level
    if (!metadata.specialMessageArtifacts) {
      const specialArtifactsMatch = partialJson.match(
        /"specialMessageArtifacts"\s*:\s*(\[[^\]]*\])/,
      );
      if (specialArtifactsMatch && specialArtifactsMatch[1]) {
        try {
          metadata.specialMessageArtifacts = JSON.parse(
            specialArtifactsMatch[1],
          );
        } catch {
          // Ignore if can't parse the array
        }
      }
    }

    return metadata;
  } catch (error) {
    console.error('Error extracting metadata from partial JSON:', error);
    return {};
  }
};

/**
 * Formats the agent label for display
 * If name is undefined, shows just the agent_type
 * If name and agent_type are the same, shows just the name
 * Otherwise shows "name (type)" with "Manager" removed from type
 *
 * @param agent The agent object or agent properties
 * @returns Formatted label string
 */
export const formatAgentLabel = (agent: {
  name?: string;
  nickname?: string;
  agent_type: string;
}): string => {
  // If name is undefined, just show agent_type
  if (!agent.name) {
    return agent.agent_type;
  }

  // If name is the same as agent_type, just show name
  if (agent.name === agent.agent_type) {
    return agent.name;
  }

  // Otherwise show "name (type)" with "Manager" removed from type
  const simplifiedType = agent.agent_type.replace('Manager', '').trim();
  return `${agent.nickname || agent.name} (${simplifiedType})`;
};

/**
 * Converts RGB to HSL
 * @param r Red component (0-255)
 * @param g Green component (0-255)
 * @param b Blue component (0-255)
 * @returns Array of [h, s, l] where h is in degrees (0-360) and s, l are in the range 0-1
 */
export const rgbToHsl = (
  r: number,
  g: number,
  b: number,
): [number, number, number] => {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b),
    min = Math.min(r, g, b);
  let h = 0,
    s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }
  return [h * 360, s, l];
};

/**
 * Converts HSL to RGB
 * @param h Hue in degrees (0-360)
 * @param s Saturation (0-1)
 * @param l Lightness (0-1)
 * @returns Array of [r, g, b] in the range 0-255
 */
export const hslToRgb = (
  h: number,
  s: number,
  l: number,
): [number, number, number] => {
  h /= 360;

  const hue2rgb = (p: number, q: number, t: number): number => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };

  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;

  const r = hue2rgb(p, q, h + 1 / 3);
  const g = hue2rgb(p, q, h);
  const b = hue2rgb(p, q, h - 1 / 3);

  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
};

/**
 * Parses a hex color string to RGB values
 * @param hex Hex color string (e.g. #FF0000, #F00, etc.)
 * @returns Array of [r, g, b] in the range 0-255, or null if invalid
 */
export const parseHexColor = (hex: string): [number, number, number] | null => {
  hex = hex.replace(/^#/, '');

  if (hex.length === 3) {
    // #RGB → #RRGGBB
    hex = hex
      .split('')
      .map((c) => c + c)
      .join('');
  } else if (hex.length === 4) {
    // #RGBA → #RRGGBBAA (ignore alpha)
    hex = hex
      .slice(0, 3)
      .split('')
      .map((c) => c + c)
      .join('');
  } else if (hex.length === 8) {
    // #RRGGBBAA → strip alpha
    hex = hex.slice(0, 6);
  }

  if (hex.length !== 6) return null;

  const bigint = parseInt(hex, 16);
  return [(bigint >> 16) & 255, (bigint >> 8) & 255, bigint & 255];
};

/**
 * Parses any CSS color string to RGB values
 * @param input CSS color string (hex, rgb, color name, etc.)
 * @returns Array of [r, g, b] in the range 0-255, or null if invalid
 */
export const parseColor = (input: string): [number, number, number] | null => {
  if (input.startsWith('#')) {
    return parseHexColor(input);
  }

  // Fallback: use canvas to parse CSS color names and rgb()
  const ctx = document.createElement('canvas').getContext('2d');
  if (!ctx) return null;

  ctx.fillStyle = input;
  const computed = ctx.fillStyle;

  if (computed.startsWith('#')) {
    return parseHexColor(computed);
  }

  return null;
};

/**
 * Converts RGB values to hex color string
 * @param rgb Array of [r, g, b] in the range 0-255
 * @returns A hex color string (e.g. #FF0000)
 */
export const toHex = (rgb: [number, number, number]): string => {
  return `#${rgb.map((v) => v.toString(16).padStart(2, '0')).join('')}`;
};

const toP3 = (rgb: [number, number, number]): string => {
  return rgb.map((v) => (v / 255).toFixed(4)).join(' ');
};

/**
 * Generates a gradient background and border color based on a predominant color
 * @param predominantColour The predominant color to base the gradient on (hex, rgb, etc.)
 * @returns An object with background and borderColor properties as CSS values
 */
export const generateMessageColors = (
  predominantColour: string,
): { background: string; borderColor: string; boxShadow: string } => {
  const rgb = parseColor(predominantColour);

  if (!rgb || rgb.length !== 3) {
    // Fallback to a default color if parsing fails
    return {
      background: '#f8f9fa',
      borderColor: '#dee2e6',
      boxShadow: '',
    };
  }

  const [h, s, _] = rgbToHsl(...rgb);
  const baseL = 0.85; // consistent perceived brightness
  const lighter = hslToRgb(h, s, baseL + 0.1);
  const base = hslToRgb(h, s, baseL);
  const darker = hslToRgb(h, s, baseL - 0.3);

  const darkerP3 = `color(display-p3 ${toP3(darker)})`;

  return {
    background: `linear-gradient(90deg, ${toHex(lighter)}, ${toHex(base)})`,
    borderColor: toHex(darker),
    boxShadow: `
    inset 0 0 0 1px color-mix(in srgb, ${darkerP3} 90%, transparent)
    `,
  };
};

/**
 * Generates base, darker, and lighter color variants from a predominant color
 * @param predominantColour The predominant color to base the colors on (hex, rgb, etc.)
 * @returns An object with base, darker, and lighter color variants as hex strings
 */
export const generateColorVariants = (
  predominantColour: string,
): { base: string; midDarker: string; darker: string; lighter: string } => {
  const rgb = parseColor(predominantColour);

  if (!rgb || rgb.length !== 3) {
    // Fallback to default blue variants if parsing fails
    return {
      base: '#97D5FC',
      midDarker: '#4A90C2',
      darker: '#4A90C2',
      lighter: '#E3F2FD',
    };
  }

  const [h, s, _] = rgbToHsl(...rgb);
  const baseL = 0.85; // base lightness
  const base = hslToRgb(h, s, baseL);
  const midDarker = hslToRgb(h, s, baseL - 0.3);
  const darker = hslToRgb(h, s, baseL - 0.6);
  const lighter = hslToRgb(h, s, baseL + 0.1);

  return {
    base: toHex(base),
    midDarker: toHex(midDarker),
    darker: toHex(darker),
    lighter: toHex(lighter),
  };
};

/**
 * Generates colors for tool messages based on a predominant color
 * @param predominantColour The predominant color to base the colors on (hex, rgb, etc.)
 * @returns An object with background and borderColor properties as CSS values
 */
export const generateToolMessageColors = (
  predominantColour: string,
): { background: string; borderColor: string; alternate: string } => {
  const rgb = parseColor(predominantColour);

  if (!rgb || rgb.length !== 3) {
    // Fallback to a default color if parsing fails
    return {
      background: '#343a40',
      borderColor: '#6c757d',
      alternate: '#343a40',
    };
  }

  const [h, s, _] = rgbToHsl(...rgb);
  const baseL = 0.85; // consistent perceived brightness
  const base = hslToRgb(h, s, baseL);
  const darker = hslToRgb(h, s, baseL - 0.6);
  const lighter = hslToRgb(h, s, baseL + 0.1);

  return {
    background: toHex(darker),
    borderColor: toHex(darker),
    alternate: toHex(lighter),
  };
};

/**
 * Calculates a lightened version of a hex color (solid, not transparent)
 * This creates a solid color that appears similar to applying opacity over white
 *
 * @param hexColor The hex color to lighten (with or without # prefix)
 * @param factor The lightening factor (0-1, where 1 is white)
 * @returns A hex color string representing the lightened color
 */
export const calculateLightenedColor = (
  hexColor: string,
  factor: number,
): string => {
  // Default to a light gray if no color is provided
  if (!hexColor) return '#f0f0f0';

  // Handle colors with or without the # prefix
  const hex = hexColor.startsWith('#') ? hexColor.substring(1) : hexColor;

  // Parse the hex color to RGB
  let r = parseInt(hex.substring(0, 2), 16) || 0;
  let g = parseInt(hex.substring(2, 4), 16) || 0;
  let b = parseInt(hex.substring(4, 6), 16) || 0;

  // Calculate the lightened color (mix with white)
  r = Math.round(r + (255 - r) * factor);
  g = Math.round(g + (255 - g) * factor);
  b = Math.round(b + (255 - b) * factor);

  // Convert back to hex
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

/**
 * Merges new threads with existing threads, preserving existing threads and adding new ones.
 * Updates existing threads with new data and sorts the result by last_updated_at.
 *
 * @param existingThreads Current threads in state
 * @param newThreads New threads from API response
 * @returns Merged and sorted thread array
 */
export const mergeThreads = (
  existingThreads: AgentThread[],
  newThreads: AgentThread[],
): AgentThread[] => {
  // Create a map of existing threads by ID for quick lookup
  const existingThreadsMap = new Map(
    existingThreads.map((thread) => [thread.id, thread]),
  );

  // Update existing threads and add new ones
  newThreads.forEach((thread) => {
    existingThreadsMap.set(thread.id, thread);
  });

  // Convert map back to array and sort by last_updated_at
  return Array.from(existingThreadsMap.values()).sort(
    (a, b) =>
      new Date(b.last_updated_at).getTime() -
      new Date(a.last_updated_at).getTime(),
  );
};
